import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Plus, TableIcon, AlertCircle, Loader2 } from 'lucide-react';
import { posRestaurant } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import { useTables } from './hooks/useTables';

// ─── Status styling ────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  available: 'border-green-400 bg-green-50 text-green-800 hover:bg-green-100 cursor-pointer',
  occupied:  'border-red-300  bg-red-50   text-red-400   cursor-not-allowed opacity-70',
  reserved:  'border-yellow-400 bg-yellow-50 text-yellow-700 cursor-not-allowed opacity-70',
};

// ─── Inline Create Table Form ──────────────────────────────────────────────────
function CreateTableForm({ existingSections, onCreated, onCancel }) {
  const { posToken } = usePosStore();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [section, setSection] = useState('');
  const [capacity, setCapacity] = useState('4');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSectionSuggestions, setShowSectionSuggestions] = useState(false);

  const suggestions = useMemo(
    () => existingSections.filter(s => s && s.toLowerCase().startsWith(section.toLowerCase())),
    [existingSections, section]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await posRestaurant.createTable(posToken, {
        name,
        section,
        capacity: parseInt(capacity) || 4,
      });
      // Immediately refresh the tables list
      await queryClient.invalidateQueries({ queryKey: ['pos', 'tables'] });
      onCreated(res.data.table);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'DUPLICATE_TABLE_NAME') {
        setError(`A table named "${name}" already exists`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to create table');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t border-gray-200 pt-4 mt-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-800">New Table</h3>

      {error && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Table Name <span className="text-red-500">*</span>
        </label>
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Table 11, Bar 3"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </div>

      <div className="relative">
        <label className="block text-xs font-semibold text-gray-700 mb-1">Section</label>
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Main, Outdoor, Bar"
          value={section}
          onChange={(e) => { setSection(e.target.value); setShowSectionSuggestions(true); }}
          onBlur={() => setTimeout(() => setShowSectionSuggestions(false), 150)}
          onFocus={() => setShowSectionSuggestions(true)}
          autoComplete="off"
        />
        {showSectionSuggestions && suggestions.length > 0 && (
          <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {suggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700"
                  onMouseDown={() => { setSection(s); setShowSectionSuggestions(false); }}
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Capacity (seats)</label>
        <input
          type="number"
          min="1"
          max="20"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={!name.trim() || loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg text-sm flex items-center justify-center gap-1 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {loading ? 'Creating…' : 'Create Table'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Pax Confirmation Panel ────────────────────────────────────────────────────
function PaxConfirm({ table, onConfirm, onBack }) {
  const [pax, setPax] = useState(1);
  const overCapacity = table.capacity && pax > table.capacity;

  return (
    <div className="flex flex-col items-center justify-center gap-5 py-8">
      <div className="text-center">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{table.section || 'Table'}</p>
        <p className="text-2xl font-bold text-gray-900">{table.name}</p>
        {table.capacity && (
          <p className="text-xs text-gray-400 mt-0.5">Max {table.capacity} seats</p>
        )}
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-700 text-center mb-3">Number of Guests</p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setPax(p => Math.max(1, p - 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            −
          </button>
          <span className="w-12 text-center text-3xl font-bold text-gray-900">{pax}</span>
          <button
            type="button"
            onClick={() => setPax(p => Math.min(50, p + 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            +
          </button>
        </div>
        {overCapacity && (
          <p className="text-xs text-amber-600 text-center mt-2">
            ⚠ Exceeds table capacity ({table.capacity})
          </p>
        )}
      </div>

      <div className="flex gap-3 w-full max-w-xs">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => onConfirm(pax)}
          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold"
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

// ─── TablePickerModal ──────────────────────────────────────────────────────────
/**
 * Props:
 *   onSelect(table, pax) — called when user confirms table + pax
 *   onClose()            — close the modal
 *   currentTableId       — id of the currently selected table (highlighted)
 */
export default function TablePickerModal({ onSelect, onClose, currentTableId }) {
  const { posStaff } = usePosStore();
  const isCashier = posStaff?.role === 'cashier';

  const { tables, isLoading, error, refetch } = useTables();
  const [showCreate, setShowCreate] = useState(false);
  const [pendingTable, setPendingTable] = useState(null); // table awaiting pax confirmation

  // Derive unique section names from existing tables for autocomplete
  const existingSections = useMemo(
    () => [...new Set(tables.map(t => t.section).filter(Boolean))].sort(),
    [tables]
  );

  // Group tables by section
  const sections = useMemo(() => {
    const map = {};
    for (const t of tables) {
      const key = t.section || 'Main';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [tables]);

  const handleCreated = (newTable) => {
    setShowCreate(false);
    // New table has no guests yet — go straight to pax step
    setPendingTable(newTable);
  };

  const handlePaxConfirm = (pax) => {
    onSelect(pendingTable, pax);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <TableIcon className="h-5 w-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">Select Table</h2>
          </div>
          <div className="flex items-center gap-2">
            {isCashier && !showCreate && !pendingTable && (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Table
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 flex-shrink-0">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-400 inline-block" /> Available
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-300 inline-block" /> Occupied
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block" /> Reserved
          </span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Pax confirmation step — shown after clicking an available table */}
          {pendingTable ? (
            <PaxConfirm
              table={pendingTable}
              onConfirm={handlePaxConfirm}
              onBack={() => setPendingTable(null)}
            />
          ) : isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 h-40 justify-center text-red-600">
              <AlertCircle className="h-6 w-6" />
              <p className="text-sm">{error}</p>
              <button onClick={refetch} className="text-sm text-blue-600 underline">Retry</button>
            </div>
          ) : tables.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
              <TableIcon className="h-8 w-8" />
              <p className="text-sm">No tables yet.</p>
              {isCashier && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Create your first table
                </button>
              )}
            </div>
          ) : showCreate && isCashier ? (
            <CreateTableForm
              existingSections={existingSections}
              onCreated={handleCreated}
              onCancel={() => setShowCreate(false)}
            />
          ) : (
            <div className="space-y-5">
              {sections.map(([section, sectionTables]) => (
                <div key={section}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {section}
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    {sectionTables.map((t) => {
                      const isSelected = t._id === currentTableId;
                      const isAvailable = t.status === 'available';
                      return (
                        <button
                          key={t._id}
                          onClick={() => isAvailable && setPendingTable(t)}
                          disabled={!isAvailable}
                          title={!isAvailable ? `${t.name} is ${t.status}` : `Select ${t.name}`}
                          className={`relative h-20 rounded-xl border-2 flex flex-col items-center justify-center gap-1 text-sm font-semibold transition-colors ${
                            STATUS_STYLE[t.status]
                          } ${isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                        >
                          <TableIcon className="h-5 w-5" />
                          <span>{t.name}</span>
                          <span className="text-xs font-normal capitalize">{t.status}</span>
                          {t.capacity && (
                            <span className="absolute top-1 right-1.5 text-[10px] text-gray-400">
                              {t.capacity}p
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
